// components/AntiCheat/AntiCheat.jsx
import { useEffect, useState, useRef } from 'react';

const AntiCheat = ({ onViolation, onTabDetected }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  /* ===========================================================
        🔵 STATUS LIST
     =========================================================== */
  const [systemStatus, setSystemStatus] = useState({
    fullscreen: false,
    devtools: false,
    screenshot: false,
    inspect: false,
    multimonitor: false,
    vm: false,
    tabSwitched: false,
  });

  // Refs untuk menyimpan interval dan timeout
  const intervalsRef = useRef([]);
  const timeoutsRef = useRef([]);
  const hasSubmittedRef = useRef(false); // Prevent multiple submissions

  useEffect(() => {
    let violationCount = 0;
    let lastViolationType = null;
    let lastViolationTime = 0;
    let warningTimeout;
    let fullscreenEnforceInterval;
    const VIOLATION_COOLDOWN = 1000;

    // Fungsi untuk membersihkan semua interval dan timeout
    const clearAllIntervalsAndTimeouts = () => {
      if (fullscreenEnforceInterval) {
        clearInterval(fullscreenEnforceInterval);
      }
      
      intervalsRef.current.forEach(interval => clearInterval(interval));
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      
      intervalsRef.current = [];
      timeoutsRef.current = [];
    };

    const showFloatingWarning = (message) => {
      setWarningMessage(message);
      setShowWarning(true);
      const hideTimeout = setTimeout(() => setShowWarning(false), 3000);
      timeoutsRef.current.push(hideTimeout);
    };

    const triggerViolation = (message, type) => {
      const now = Date.now();
      if (type === lastViolationType && (now - lastViolationTime) < VIOLATION_COOLDOWN) return;

      clearTimeout(warningTimeout);
      warningTimeout = setTimeout(() => {
        violationCount++;
        lastViolationType = type;
        lastViolationTime = now;
        
        onViolation?.(violationCount);
        
        // Hanya tampilkan warning untuk violation non-tab
        if (type !== 'tab_switch') {
          showFloatingWarning(`${message} (Pelanggaran ${violationCount}/3)`);
        }

        if (violationCount >= 3 && type !== 'tab_switch') {
          alert('⚠️ Terlalu banyak pelanggaran! Ujian akan otomatis disubmit.');
          if (window.autoSubmitExam) window.autoSubmitExam();
        }
      }, 100);
      timeoutsRef.current.push(warningTimeout);
    };

    const isAllowedTarget = (target) => {
      return (
        target?.type === 'radio' ||
        target?.tagName === 'BUTTON' ||
        target?.closest?.('button') ||
        target?.tagName === 'INPUT' ||
        target?.tagName === 'SELECT' ||
        target?.tagName === 'TEXTAREA'
      );
    };

    const enforceFullscreen = () => {
      const elem = document.documentElement;

      if (!document.fullscreenElement) {
        if (elem.requestFullscreen) {
          elem.requestFullscreen().catch(() => {
            setSystemStatus((s) => ({ ...s, fullscreen: false }));
          });
        }
      }

      setSystemStatus((s) => ({ ...s, fullscreen: !!document.fullscreenElement }));
    };

    const blockKey = (e) => {
      // Allow F11 for fullscreen
      if (e.key === 'F11') {
        return;
      }

      if ((e.ctrlKey || e.metaKey) && (e.key === 'r' || e.key === 'R')) {
        // Allow refresh dengan Ctrl+R
        return;
      }

      if (e.ctrlKey || e.metaKey || e.altKey) {
        // Blokir shortcut umum kecuali refresh
        if (!(e.key === 'r' || e.key === 'R')) {
          e.preventDefault();
          showFloatingWarning('⚠️ Shortcut keyboard diblokir!');
          enforceFullscreen();
          return;
        }
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        showFloatingWarning('🔒 ESC DIBLOKIR PERMANEN!');
        enforceFullscreen();
        return;
      }

      if (e.key === 'Tab') {
        // Allow tab untuk navigasi form
        if (!isAllowedTarget(document.activeElement)) {
          e.preventDefault();
          showFloatingWarning('⚠️ Tab tidak diizinkan di area ini!');
        }
      }
    };

    const handleFullscreen = () => {
      if (!document.fullscreenElement) {
        showFloatingWarning('🔒 KEMBALI KE FULLSCREEN...');
        enforceFullscreen();
      }
      setSystemStatus((s) => ({ ...s, fullscreen: !!document.fullscreenElement }));
    };

    // ⚠️ STRICT MODE: LANGSUNG SUBMIT SAAT BUKA TAB
    const handleVisibility = () => {
      if (document.hidden) {
        // Prevent multiple submissions
        if (hasSubmittedRef.current) return;
        
        hasSubmittedRef.current = true;
        
        // Update status
        setSystemStatus((s) => ({ ...s, tabSwitched: true }));
        onTabDetected?.(true);
        
        // Tampilkan warning merah
        showFloatingWarning('🚨 TAB SWITCH TERDETEKSI! UJIAN AKAN DISUBMIT!');
        
        // Submit otomatis setelah 1 detik
        setTimeout(() => {
          alert('⛔ PELANGGARAN BERAT!\n\nAnda membuka tab/aplikasi lain.\nUjian akan otomatis disubmit sekarang!');
          
          // Panggil fungsi submit
          if (window.autoSubmitExam) {
            window.autoSubmitExam();
          } else {
            console.error('⚠️ window.autoSubmitExam() tidak ditemukan!');
          }
        }, 1000);
        
      } else {
        onTabDetected?.(false);
      }
    };

    const handleBlur = () => {
      // Deteksi jika user pindah ke aplikasi lain (bukan hanya tab browser)
      if (document.visibilityState === 'visible') {
        if (hasSubmittedRef.current) return;
        
        hasSubmittedRef.current = true;
        
        showFloatingWarning('🚨 APLIKASI LAIN TERDETEKSI! UJIAN AKAN DISUBMIT!');
        
        setTimeout(() => {
          alert('⛔ PELANGGARAN BERAT!\n\nAnda membuka aplikasi lain.\nUjian akan otomatis disubmit sekarang!');
          
          if (window.autoSubmitExam) {
            window.autoSubmitExam();
          }
        }, 1000);
      }
    };

    const blockAction = (e) => {
      e.preventDefault();

      if (e.type === 'contextmenu') showFloatingWarning('⚠️ Right-click diblokir!');
      if (e.type === 'copy') showFloatingWarning('⚠️ Copy diblokir!');
      if (e.type === 'cut') showFloatingWarning('⚠️ Cut diblokir!');
      if (e.type === 'paste') showFloatingWarning('⚠️ Paste diblokir!');
    };

    let lastClickTime = 0;
    const handleClick = (e) => {
      const now = Date.now();
      if (isAllowedTarget(e.target)) {
        lastClickTime = now;
        return;
      }
      if (now - lastClickTime < 300) {
        e.preventDefault();
        showFloatingWarning('⚠️ Double-click diblokir!');
      }
      lastClickTime = now;
    };

    // Inisialisasi fullscreen
    enforceFullscreen();

    // Setup interval untuk enforce fullscreen
    fullscreenEnforceInterval = setInterval(() => {
      if (!document.fullscreenElement) {
        enforceFullscreen();
      }
    }, 1000);

    // Event listeners
    const events = [
      { element: document, type: 'click', handler: handleClick, options: true },
      { element: document, type: 'keydown', handler: blockKey, options: true },
      { element: document, type: 'keyup', handler: blockKey, options: true },
      { element: document, type: 'keypress', handler: blockKey, options: true },
      { element: document, type: 'contextmenu', handler: blockAction, options: true },
      { element: document, type: 'selectstart', handler: blockAction, options: true },
      { element: document, type: 'copy', handler: blockAction, options: true },
      { element: document, type: 'cut', handler: blockAction, options: true },
      { element: document, type: 'paste', handler: blockAction, options: true },
      { element: document, type: 'fullscreenchange', handler: handleFullscreen },
      { element: document, type: 'visibilitychange', handler: handleVisibility },
      { element: window, type: 'blur', handler: handleBlur },
    ];

    events.forEach(({ element, type, handler, options }) => {
      element.addEventListener(type, handler, options);
    });

    /* ===========================================================
          🔐 FITUR TAMBAHAN
       =========================================================== */

    // 1. Deteksi Developer Tools
    const detectDevTools = () => {
      const threshold = 160;
      const widthDiff = window.outerWidth - window.innerWidth;
      const heightDiff = window.outerHeight - window.innerHeight;
      
      if (widthDiff > threshold || heightDiff > threshold) {
        triggerViolation("⚠️ Developer Tools terdeteksi!", "devtools");
        setSystemStatus((s) => ({ ...s, devtools: true }));
      } else {
        setSystemStatus((s) => ({ ...s, devtools: false }));
      }
    };
    
    const devToolsInterval = setInterval(detectDevTools, 800);
    intervalsRef.current.push(devToolsInterval);

    // 2. Deteksi Screenshot
    const handleScreenshot = (e) => {
      if (e.key === "PrintScreen") {
        triggerViolation("⚠️ Screenshot tidak diizinkan!", "screenshot");
        setSystemStatus((s) => ({ ...s, screenshot: true }));
        
        setTimeout(() => {
          setSystemStatus((s) => ({ ...s, screenshot: false }));
        }, 5000);
      }
    };
    window.addEventListener("keyup", handleScreenshot);

    // 3. Blok Inspect Element
    const handleInspect = (e) => {
      if (
        (e.ctrlKey && e.shiftKey && e.key === "I") ||
        (e.ctrlKey && e.shiftKey && e.key === "C") ||
        (e.ctrlKey && e.key === "U") ||
        (e.key === "F12")
      ) {
        e.preventDefault();
        showFloatingWarning("⚠️ Inspect diblokir!");
        setSystemStatus((s) => ({ ...s, inspect: true }));
        
        setTimeout(() => {
          setSystemStatus((s) => ({ ...s, inspect: false }));
        }, 5000);
      }
    };
    window.addEventListener("keydown", handleInspect);

    // 4. Deteksi Multi Monitor
    const detectMultiMonitor = () => {
      const screenWidth = window.screen?.width || 0;
      
      if (screenWidth > 0 && window.innerWidth < screenWidth * 0.9) {
        triggerViolation("⚠️ Multi-monitor terdeteksi!", "multi_monitor");
        setSystemStatus((s) => ({ ...s, multimonitor: true }));
      } else {
        setSystemStatus((s) => ({ ...s, multimonitor: false }));
      }
    };
    
    const multiMonitorInterval = setInterval(detectMultiMonitor, 1500);
    intervalsRef.current.push(multiMonitorInterval);

    // 5. Deteksi Virtual Machine
    const detectVM = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes("vmware") || userAgent.includes("virtualbox")) {
        showFloatingWarning("⚠️ Lingkungan virtual terdeteksi!");
        setSystemStatus((s) => ({ ...s, vm: true }));
      }
    };
    
    detectVM();

    return () => {
      // Cleanup
      clearAllIntervalsAndTimeouts();
      
      events.forEach(({ element, type, handler, options }) => {
        element.removeEventListener(type, handler, options);
      });
      
      window.removeEventListener("keyup", handleScreenshot);
      window.removeEventListener("keydown", handleInspect);
    };
  }, [onViolation, onTabDetected]);

  /* ===========================================================
        🔵 TAMPILAN UI
     =========================================================== */
  return (
    <>
      {/* Floating Warning */}
      {showWarning && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: systemStatus.tabSwitched ? '#d32f2f' : '#ff5252',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 99999,
          maxWidth: '350px',
          fontWeight: 'bold',
          animation: 'fadeIn 0.3s ease-in',
          fontSize: '14px'
        }}>
          {warningMessage}
        </div>
      )}

      {/* PANEL STATUS */}
      <div style={{
        position: "fixed",
        bottom: "20px",
        right: "20px",
        background: systemStatus.tabSwitched ? "#ffebee" : "#fff",
        padding: "14px 20px",
        borderRadius: "10px",
        boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
        zIndex: 9999,
        width: "280px",
        border: systemStatus.tabSwitched ? "2px solid #d32f2f" : "1px solid #ddd",
        fontSize: "13px"
      }}>
        <h4 style={{ 
          marginBottom: 10, 
          marginTop: 0,
          color: systemStatus.tabSwitched ? "#d32f2f" : "#333"
        }}>
          {systemStatus.tabSwitched ? "⛔ PELANGGARAN TERDETEKSI!" : "🔒 Status Pemeriksaan"}
        </h4>

        <p>🖥️ Fullscreen: {systemStatus.fullscreen ? "✅" : "❌"}</p>
        <p>🛠️ DevTools: {systemStatus.devtools ? "❌ Terdeteksi" : "✅ Aman"}</p>
        <p>📸 Screenshot: {systemStatus.screenshot ? "❌ Melanggar" : "✅ Aman"}</p>
        <p>🔍 Inspect: {systemStatus.inspect ? "❌ Melanggar" : "✅ Aman"}</p>
        <p>🖥️🖥️ Multi Monitor: {systemStatus.multimonitor ? "❌ Detected" : "✅ Single"}</p>
        <p>🧪 Virtual Machine: {systemStatus.vm ? "❌ VM" : "✅ Native"}</p>
        
        <hr style={{ margin: "10px 0", border: "none", borderTop: "1px solid #ddd" }} />
        
        <p style={{ 
          color: systemStatus.tabSwitched ? "#d32f2f" : "#388e3c",
          fontWeight: "bold",
          fontSize: "14px",
          margin: 0
        }}>
          {systemStatus.tabSwitched ? "🚨 TAB SWITCH: MELANGGAR!" : "📑 Tab Status: ✅ Aman"}
        </p>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default AntiCheat;